import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { BookOpen, Users, Award, ArrowRight, CheckCircle } from 'lucide-react';

const LandingPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const features = [
    {
      icon: BookOpen,
      title: 'Formations de qualité',
      description: 'Des programmes certifiés adaptés aux professionnels de l\'éducation'
    },
    {
      icon: Users,
      title: 'Accompagnement personnalisé',
      description: 'Un suivi individuel par des formateurs experts du domaine'
    },
    {
      icon: Award,
      title: 'Certification reconnue',
      description: 'Obtenez des certificats valorisants pour votre carrière'
    }
  ];

  const benefits = [
    'Plus de 50 programmes de formation',
    'Formateurs certifiés RNCP & ICPF',
    'Organisme certifié Qualiopi',
    'Flexibilité d\'apprentissage',
    '25+ ans d\'expérience cumulée',
    'France & Cameroun'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/affea-logo.png" alt="AFFEA" className="w-10 h-10 rounded-lg object-cover" />
            <div>
              <h1 className="font-display text-xl font-bold text-navy-700">AFFEA</h1>
              <p className="text-xs text-teal-600 italic">Académie Francophone de la Formation, de l'Enseignement et de l'Accompagnement</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://affea.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-slate-600 hover:text-navy-700 transition-colors hidden sm:inline-flex"
            >
              Site AFFEA
            </a>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-navy-700 to-teal-600 hover:from-navy-800 hover:to-teal-700 text-white btn-scale"
              data-testid="header-login-btn"
            >
              Se connecter
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4" style={{ background: 'linear-gradient(135deg, #0a3d62 0%, #0c2d48 50%, #38ada9 100%)' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <p className="text-teal-300 font-semibold mb-4 tracking-wide uppercase text-sm">
                Plateforme d'apprentissage AFFEA
              </p>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 italic">
                Développez vos{' '}
                <span className="text-teal-300">compétences</span>{' '}
                professionnelles
              </h1>
              <p className="text-lg text-white/80 mb-8 leading-relaxed">
                AFFEA accompagne les professionnels de nombreux secteurs d'activité dans leur développement : 
                Formations qualifiantes, certifiantes ou diplômantes, accompagnement personnalisé et expertise reconnue.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate('/login')}
                  size="lg"
                  className="bg-teal-500 hover:bg-teal-600 text-white px-8 btn-scale"
                  data-testid="hero-login-btn"
                >
                  Accéder à la plateforme
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-2 border-white/30 text-white hover:bg-white/10 px-8"
                  onClick={() => window.open('https://affea.fr', '_blank')}
                  data-testid="learn-more-btn"
                >
                  Découvrir AFFEA
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in hidden md:block" style={{ animationDelay: '0.2s' }}>
              <div className="absolute -top-4 -left-4 w-72 h-72 bg-teal-500/20 rounded-full blur-3xl" />
              <img
                src="https://images.unsplash.com/photo-1622675363311-3e1904dc1885?w=800&q=80"
                alt="Formation professionnelle multi-raciale"
                className="relative rounded-2xl shadow-2xl w-full object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Certifié Qualiopi</p>
                    <p className="text-sm text-slate-500">Qualité attestée</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-700 mb-4 italic">
              Pourquoi choisir AFFEA ?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Une approche pédagogique centrée sur l'excellence et l'accompagnement personnalisé
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-sm border border-slate-100 card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-xl bg-teal-50 flex items-center justify-center mb-6">
                  <feature.icon className="h-7 w-7 text-teal-600" />
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="/expertise-learning.jpg"
                alt="Environnement d'apprentissage diversifié"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-700 mb-6 italic">
                Une expertise reconnue
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Depuis 2018, AFFEA forme les professionnels de l'éducation avec une approche 
                innovante et des méthodes pédagogiques éprouvées.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-teal-500 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #0a3d62 0%, #0c2d48 60%, #38ada9 100%)' }}>
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6 italic">
            Prêt à développer vos compétences ?
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Rejoignez notre communauté d'apprenants et transformez votre pratique professionnelle.
          </p>
          <Button 
            onClick={() => navigate('/login')}
            size="lg"
            className="bg-teal-500 hover:bg-teal-600 text-white px-10 py-6 text-lg btn-scale"
            data-testid="cta-login-btn"
          >
            Accéder à la plateforme
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12" style={{ background: '#0a1628' }}>
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/affea-logo.png" alt="AFFEA" className="w-10 h-10 rounded-lg object-cover" />
              <div>
                <h3 className="font-display text-lg font-bold text-white">AFFEA</h3>
                <p className="text-xs text-slate-400">Académie Francophone de la Formation, de l'Enseignement et de l'Accompagnement</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-slate-400 text-sm">
              <a href="https://affea.fr" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
                Site officiel AFFEA
              </a>
              <span>|</span>
              <span>&copy; {new Date().getFullYear()} AFFEA. Tous droits réservés.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
